import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Button,
  TextField,
  MenuItem,
  Stack,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import config from '../../Config';

const accessLevels = ['Admin', 'Standard'];

export default function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formVisible, setFormVisible] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    password: '',
    accessLevel: '',
  });

  const navigate = useNavigate();

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/get`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setAdmins(data.admin);
      } else {
        throw new Error(data.message || 'Failed to fetch admins');
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAdmin((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/auth/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newAdmin),
      });

      const data = await res.json();

      if (res.ok) {
        Swal.fire({ icon: 'success', title: 'Success', text: 'Admin created successfully!' });
        setNewAdmin({ name: '', email: '', password: '', accessLevel: '' });
        fetchAdmins();
        setFormVisible(false);
      } else {
        throw new Error(data.message || 'Failed to create admin');
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteAdmin = async (id) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: 'This admin will be deleted permanently!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });

    if (!confirm.isConfirmed) return;

    setDeleteLoadingId(id);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/delete/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (res.ok) {
        Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Admin has been deleted.' });
        fetchAdmins();
      } else {
        throw new Error(data.message || 'Failed to delete admin');
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: err.message });
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const renderSkeletonTable = () => (
    <TableContainer component={Paper} elevation={3}>
      <Table>
        <TableHead>
          <TableRow>
            {['Name', 'Email', 'Access Level', 'Created At', 'Actions'].map((heading, index) => (
              <TableCell key={index}>
                <Skeleton variant="text" width={100} height={24} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(5)].map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {[...Array(5)].map((_, colIndex) => (
                <TableCell key={colIndex}>
                  <Skeleton variant="text" height={20} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ marginTop: '10px' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Admin Management
        </Typography>

        {!formVisible && (
          <Button variant="contained" onClick={() => setFormVisible(true)}>
            Add Admin
          </Button>
        )}
      </Stack>

      {/* Add Admin Form */}
      {formVisible && (
        <Paper sx={{ p: 3, mb: 4 }} elevation={3}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <IconButton onClick={() => setFormVisible(false)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={600}>
              Add New Admin
            </Typography>
          </Stack>

          <Box
            component="form"
            onSubmit={handleAddAdmin}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              label="Name"
              name="name"
              value={newAdmin.name}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              value={newAdmin.email}
              onChange={handleInputChange}
              required
              type="email"
              fullWidth
            />
            <TextField
              label="Password"
              name="password"
              value={newAdmin.password}
              onChange={handleInputChange}
              required
              type="password"
              fullWidth
            />
            <TextField
              label="Access Level"
              name="accessLevel"
              value={newAdmin.accessLevel}
              onChange={handleInputChange}
              required
              select
              fullWidth
            >
              {accessLevels.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </TextField>

            <Button type="submit" variant="contained" disabled={formLoading}>
              {formLoading ? <CircularProgress size={24} /> : 'Create Admin'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Admin Table */}
      {!formVisible &&
        (loading ? (
          renderSkeletonTable()
        ) : (
          <TableContainer component={Paper} elevation={3}>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell>
                    <strong>Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Email</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Access Level</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Created At</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin._id}>
                    <TableCell>
                      <Typography
                        sx={{
                          color: 'primary.main',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        onClick={() => navigate(`/admin-detail/${admin._id}`)}
                      >
                        {admin.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>{admin.accessLevel}</TableCell>
                    <TableCell>{new Date(admin.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteAdmin(admin._id)}
                        disabled={deleteLoadingId === admin._id}
                      >
                        {deleteLoadingId === admin._id ? (
                          <CircularProgress size={24} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))}
    </Box>
  );
}
